import { PermissionsTypes, UserCombinePermissions } from '@back-office/http';
import { Button } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import HighlightOffOutlinedIcon from '@material-ui/icons/HighlightOffOutlined';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { EditOutlined, TrashIcon } from '../../icons';
import { getDatetimeText } from '../../utils';
import GcpBox from '../gcp-box';
import GcpClientPagination from '../gcp-cilent-pagination';
import { useInvitationStyles } from '../gcp-invitaion-table';
import GcpPermissionDropdown from '../gcp-permissions-dropdown';
import GcpPhoneNumberField from '../gcp-phone-number-field';
import GcpTypography from '../gcp-typography';
import useViewModel from './viewmodel';

export interface GcpUserTableProps {
  data: UserCombinePermissions[];
  permissionsList: PermissionsTypes[];
  isRecurringMerchant: boolean;
  handleOnClickRemove: (user: UserCombinePermissions) => void;
  refresh?: () => void;
}

export type PopupState = {
  show: boolean;
  isSuccess: boolean;
};

export type PopupStyle = {
  backgroundColor: string;
  message: string;
};

const GcpUserTable: FC<GcpUserTableProps> = observer((props) => {
  const {
    data,
    handleOnClickRemove,
    refresh,
    permissionsList,
    isRecurringMerchant,
  } = props;

  const classes = useInvitationStyles();
  const { t } = useTranslation();

  const {
    getRoleColor,
    handleEditButtonClick,
    handleSaveButtonClick,
    handlePhoneNumberFieldChange,
    handlePermissionFieldChange,
    handleChangePage,
    getIconStyle,
    getIconStyleTrash,
    anyEditingRow,
    enableSaveButton,
    editedPhoneNumber,
    popupState,
    getPopupProps,
    sortUserRoleAndPermission,
    page,
    rowsPerPage,
    isAdmin,
    editingRow,
    convertToArray,
    setEditingRow,
    setEnableSaveButton,
  } = useViewModel({
    isRecurringMerchant,
    refresh,
  });

  return (
    <TableContainer component="div">
      <GcpBox
        className={classes.success_popup}
        hidden={!popupState?.show}
        style={{ backgroundColor: getPopupProps().backgroundColor }}
      >
        {getPopupProps().message}
      </GcpBox>
      <Table aria-label="GCP User Table" className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell
              align="center"
              className={classes.headerCell}
              style={{ verticalAlign: 'top !important' }}
              width="12%"
            >
              <GcpBox ml={7}>
                <GcpTypography paint="hitGray" variant="body1">
                  {t('invite_new_user.email')}
                </GcpTypography>
              </GcpBox>
            </TableCell>
            <TableCell
              align="center"
              className={classes.headerCell}
              width="20%"
            >
              <GcpTypography paint="hitGray" variant="body1">
                {t('invite_new_user.mobile_number')}
              </GcpTypography>
            </TableCell>
            <TableCell
              align="center"
              className={classes.headerCell}
              width="10%"
            >
              <GcpTypography paint="hitGray" variant="body1">
                {t('invite_new_user.roles')}
              </GcpTypography>
            </TableCell>
            <TableCell
              align="center"
              className={classes.headerCell}
              width="20%"
            >
              <GcpTypography paint="hitGray" variant="body1">
                {t('invite_new_user.permission')}
              </GcpTypography>
            </TableCell>
            <TableCell
              align="center"
              className={classes.headerCell}
              width="15%"
            >
              <GcpTypography paint="hitGray" variant="body1">
                {t('invite_new_user.invited_at')}
              </GcpTypography>
            </TableCell>
            <TableCell align="left" className={classes.headerCell} width="26%">
              <GcpTypography paint="hitGray" variant="body1">
                {t('invite_new_user.created_at')}
              </GcpTypography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortUserRoleAndPermission(
            data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          ).map((row) => (
            <TableRow key={row.email}>
              <TableCell
                className={(classes.cell, classes.top_align)}
                component="th"
                scope="row"
              >
                <GcpTypography
                  paint={getRoleColor(row.role)}
                  variant="body1"
                  wordBreak="break-all"
                >
                  {row.email}
                </GcpTypography>
              </TableCell>
              <TableCell align="center" className={classes.top_align}>
                <GcpPhoneNumberField
                  hiddenField={!editingRow.get(row.email)}
                  phoneNumber={row.phone}
                  phoneNumberTextColor={getRoleColor(row.role)}
                  value={editedPhoneNumber}
                  onFieldChange={handlePhoneNumberFieldChange}
                />
              </TableCell>
              <TableCell align="center" className={classes.top_align}>
                <GcpTypography paint={getRoleColor(row.role)} variant="body1">
                  {row.role}
                </GcpTypography>
              </TableCell>
              <TableCell className={classes.top_align}>
                {isAdmin(row.role) ? (
                  <>
                    <GcpTypography
                      paint={getRoleColor(row.role)}
                      variant="body1"
                    >
                      {t('invite_new_user.admin_void_refund_permissions')}
                      {isRecurringMerchant && ','}
                    </GcpTypography>
                    {isRecurringMerchant && (
                      <>
                        <GcpTypography
                          paint={getRoleColor(row.role)}
                          variant="body1"
                        >
                          {t('invite_new_user.admin_enrollment_permissions')}
                        </GcpTypography>
                        <GcpTypography
                          paint={getRoleColor(row.role)}
                          variant="body1"
                        >
                          {t('invite_new_user.admin_payment_file_permissions')}
                        </GcpTypography>
                      </>
                    )}
                  </>
                ) : (
                  permissionsList && (
                    <GcpTypography
                      paint={getRoleColor(row.role)}
                      variant="body1"
                    >
                      <GcpPermissionDropdown
                        currentPermission={convertToArray(row.permissionId)}
                        handleOnChange={handlePermissionFieldChange}
                        hiddenField={!editingRow.get(row.email)}
                        isRecurringMerchant={isRecurringMerchant}
                        permissionsList={permissionsList}
                        voidRefundCount={data.reduce((count, item) => {
                          if (
                            item.role !== 'ADMIN' &&
                            item.permissionId.includes(1)
                          ) {
                            return count + 1;
                          }
                          return count;
                        }, 0)}
                      />
                    </GcpTypography>
                  )
                )}
              </TableCell>
              <TableCell align="left" className={classes.top_align}>
                <GcpTypography paint={getRoleColor(row.role)} variant="body1">
                  {getDatetimeText(row.invitedAt, 'default')}
                </GcpTypography>
              </TableCell>
              <TableCell align="left" className={classes.top_align}>
                <GcpBox
                  alignItems="center"
                  display="flex"
                  justifyContent="space-between"
                >
                  <GcpTypography paint={getRoleColor(row.role)} variant="body1">
                    {getDatetimeText(row.createdAt, 'default')}
                  </GcpTypography>
                  {editingRow.get(row.email) ? (
                    <>
                      &nbsp;&nbsp;
                      <Button
                        className={clsx(classes.secondary, {
                          [classes.hidden]: isAdmin(row.role),
                        })}
                        disabled={!enableSaveButton}
                        variant="outlined"
                        onClick={() => handleSaveButtonClick(row)}
                      >
                        {t('invite_new_user.save')}
                      </Button>
                      &nbsp;
                      <Button
                        className={clsx(classes.warning, {
                          [classes.hidden]: isAdmin(row.role),
                        })}
                        onClick={() => {
                          setEditingRow(new Map());
                          setEnableSaveButton(false);
                        }}
                      >
                        <HighlightOffOutlinedIcon className={classes.close} />
                      </Button>
                    </>
                  ) : (
                    <>
                      &nbsp;&nbsp;
                      <Button
                        className={clsx(classes.secondary, {
                          [classes.hidden]: isAdmin(row.role),
                        })}
                        disabled={anyEditingRow()}
                        variant="outlined"
                        onClick={() => handleOnClickRemove(row)}
                      >
                        <TrashIcon
                          className={`${classes.icons} trash`}
                          fontSize="small"
                          style={getIconStyleTrash()}
                        />
                      </Button>
                      &nbsp;
                      <Button
                        className={clsx(classes.secondary, {
                          [classes.hidden]: isAdmin(row.role),
                        })}
                        disabled={anyEditingRow()}
                        variant="outlined"
                        onClick={() => handleEditButtonClick(row)}
                      >
                        <EditOutlined
                          className={classes.icons}
                          fontSize="small"
                          style={getIconStyle()}
                        />
                      </Button>
                    </>
                  )}
                </GcpBox>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {data.length !== 0 && (
        <GcpClientPagination
          handleChangePage={handleChangePage}
          page={page}
          rowsPerPage={rowsPerPage}
          total={data.length}
        />
      )}
    </TableContainer>
  );
});

export default GcpUserTable;
